import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePostForm, SEASONS } from "../hooks/usePostForm";
import { VideoPicker, PhotoPicker, DeviceImport } from "../components/create/MediaSections";
import { Sparkles } from "lucide-react";

const inputCls =
  "w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-3 rounded-sm text-white transition-colors";

export default function CreatePostPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const {
    fields, setField, file, setFile, photos, setPhotos,
    uploadProgress, uploading, aiLoading, device, error,
    generateCaption, importDeviceFile, clearDevice, submit,
  } = usePostForm();

  useEffect(() => {
    if (user === false) nav("/login");
  }, [user, nav]);

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="create-post-page">
      <h1 className="font-display text-4xl uppercase tracking-tight text-white mb-2">
        Post a match
      </h1>
      <p className="text-sm text-[#a3a3a3] mb-6">
        Share your highlights, stats and opponent. Add a video up to 200MB.
      </p>

      <form onSubmit={submit} className="space-y-4">
        {error && (
          <div data-testid="create-post-error" className="text-sm text-red-400 border border-red-500/40 bg-red-500/10 p-3 rounded-sm">
            {error}
          </div>
        )}

        <div>
          <textarea
            data-testid="create-caption"
            value={fields.caption}
            onChange={(e) => setField("caption")(e.target.value)}
            placeholder="Match report, thoughts, thanks to teammates…"
            rows={3}
            className="w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none p-4 rounded-sm text-white transition-colors resize-none"
          />
          <div className="flex items-center justify-between mt-1">
            <span className="text-[11px] text-[#737373]">
              Fill in your stats below, then let AI write it for you.
            </span>
            <button
              type="button"
              data-testid="create-ai-caption-btn"
              onClick={generateCaption}
              disabled={aiLoading}
              className="flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest border border-[#404040] text-white hover:bg-white hover:text-black rounded-sm disabled:opacity-60 transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {aiLoading ? "Writing…" : "Generate with AI"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Labeled label="Opponent">
            <input
              data-testid="create-opponent"
              value={fields.opponent}
              onChange={(e) => setField("opponent")(e.target.value)}
              placeholder="e.g. Riverside FC"
              className={inputCls}
            />
          </Labeled>
          <Labeled label="Result">
            <input
              data-testid="create-result"
              value={fields.result}
              onChange={(e) => setField("result")(e.target.value)}
              placeholder="e.g. W 3-1"
              className={inputCls}
            />
          </Labeled>
          <Labeled label="Season">
            <select
              data-testid="create-season"
              value={fields.season}
              onChange={(e) => setField("season")(e.target.value)}
              className={inputCls}
            >
              {SEASONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Labeled>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <NumInput label="Goals" value={fields.goals} onChange={setField("goals")} testid="create-goals" />
          <NumInput label="Assists" value={fields.assists} onChange={setField("assists")} testid="create-assists" />
          <NumInput label="Minutes" value={fields.minutes} onChange={setField("minutes")} testid="create-minutes" />
        </div>

        <VideoPicker file={file} setFile={setFile} uploading={uploading} uploadProgress={uploadProgress} />
        <PhotoPicker photos={photos} setPhotos={setPhotos} />
        <DeviceImport device={device} onImport={importDeviceFile} onClear={clearDevice} />

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => nav(-1)}
            className="px-4 py-2 border border-[#262626] text-white hover:bg-[#1a1a1a] rounded-sm transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="create-submit"
            disabled={uploading}
            className="px-6 py-2 bg-white text-black font-semibold rounded-sm hover:bg-gray-200 disabled:opacity-60 transition-colors"
          >
            {uploading ? "Publishing…" : "Publish"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[#737373] mb-1">{label}</div>
      {children}
    </label>
  );
}

function NumInput({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[#737373] mb-1">{label}</div>
      <input
        data-testid={testid}
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-3 rounded-sm text-white font-display text-xl transition-colors"
      />
    </label>
  );
}
